import React from "react";
import { useOpeningHours } from "../redesign/useBusinessInfo";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { 
  XCircle, 
  ShoppingCart, 
  ArrowLeft,
  Mail
} from "lucide-react";
import SEOHead from "../components/SEOHead";

const CheckoutCancelledPage = () => {
  const openingHours = useOpeningHours();
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <SEOHead
        title="Payment Cancelled - Umoona Opal Mine"
        description="Your payment was cancelled. Your cart items are still saved. Contact us for assistance or return to continue shopping."
        canonical="/checkout/cancelled"
      />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <Card className="shadow-lg border-yellow-200">
          <CardHeader className="text-center pb-6">
            <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-yellow-600" />
            </div>
            
            <h1 className="text-3xl font-bold mb-2">
              Payment Cancelled
            </h1>
            
            <p className="text-gray-600 text-lg">
              Your payment was cancelled and no charges were made
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            
            {/* Information */}
            <div className="bg-yellow-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-3 text-yellow-800">
                What happened?
              </h3>
              
              <p className="text-yellow-700 text-sm mb-4">
                You cancelled the payment process before completing your purchase. 
                Your cart items are still saved and ready for checkout when you're ready.
              </p>
              
              <div className="text-sm text-yellow-700">
                <p className="font-medium mb-2">Common reasons for cancellation:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Changed your mind about the purchase</li>
                  <li>Wanted to review your order again</li>
                  <li>Encountered technical difficulties</li>
                  <li>Needed to update payment information</li>
                </ul>
              </div>
            </div>

            {/* Help Section */}
            <div className="bg-blue-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-600" />
                Need Assistance?
              </h3>
              
              <p className="text-blue-700 text-sm mb-4">
                If you experienced any issues during checkout or have questions about our products, 
                we're here to help!
              </p>
              
              <div className="text-sm text-blue-700 space-y-2">
                <p><strong>Phone:</strong> (08) 8672 5288</p>
                <p><strong>Email:</strong> admin@umoonaopalmine.com.au</p>
                <p><strong>Hours:</strong> {openingHours.summary.join(" · ")}</p>
              </div>
            </div>

            {/* What's Next */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold mb-3">
                What would you like to do next?
              </h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-3">
                  <ShoppingCart className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Continue Shopping</p>
                    <p className="text-gray-600">Browse our collection of authentic Coober Pedy opals</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <ArrowLeft className="w-5 h-5 text-purple-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Try Checkout Again</p>
                    <p className="text-gray-600">Your cart items are still saved and ready</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button asChild variant="outline" size="lg" className="flex items-center gap-2">
                <Link to="/opal-store">
                  <ShoppingCart className="w-4 h-4" />
                  Continue Shopping
                </Link>
              </Button>
              
              <Button asChild className="mining-cta" size="lg">
                <Link to="/opal-store/checkout">
                  Try Checkout Again
                </Link>
              </Button>
            </div>

            {/* Additional Help */}
            <div className="text-center pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500 mb-3">
                Still having trouble? We're here to help!
              </p>
              <Button asChild variant="ghost" size="sm">
                <Link to="/contact">
                  Contact Support
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CheckoutCancelledPage;